import { Fragment } from "react"
import { graphql } from "gatsby"
import { getImage } from "gatsby-plugin-image"
/** @jsx jsx */
import { jsx, Badge, BaseStyles, Box, Grid, Heading, Image, Paragraph, Text } from "theme-ui"
import { Themed } from "@theme-ui/mdx"
import {useSiteMetadata} from "../../hooks/use-site-metadata"
import Layout from '../../components/layout'
import PageLayout from '../../components/page-layout'
import Seo from "../../components/seo"

export const query = graphql`
  query($id: String) {
    strapiAssociation(id: { eq: $id }) {
      name
      category {
        name
        color
      }
      logo {
        localFile {
          childImageSharp {
            gatsbyImageData
          }
        }
      }
      shortDescription
      address
      phones {
        name
        number
      }
      contacts {
        name
        role
      }
      email
      website
      socialNetworks {
        socialNetwork
        name
        url
      }
      description {
        data {
          childMarkdownRemark {
            html
          }
        }
      }
    }
  }
`

function coordinatesLine(key, value, index = 0) {
  return <Fragment key={key + index}><strong>{key} :</strong> {value}<br/></Fragment>
}

export const Head = function ({ data, location }) {
  const { siteUrl } = useSiteMetadata()
  const { name, shortDescription, logo, address, phones, contacts, email, website, socialNetworks } = data.strapiAssociation
  const image = getImage(logo?.localFile)
  const logoUrl = image ? siteUrl + image.images.fallback.src: null

  const linkedData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: name,
    description: shortDescription,
    logo: logoUrl,
    address: address,
    contactPoint: phones?.map(phone => ({'@type': 'ContactPoint', contactType: phone.name, telephone: phone.number})),
    member: contacts?.map(contact => ({'@type': 'OrganizationRole', roleName: contact.role, member: {'@type': 'Person', name: contact.name}})),
    email: email,
    url: website || siteUrl + location.pathname,
    sameAs: socialNetworks?.map(({ url }) => url)
  }

  return (
      <Seo
        title={`${name} - Association à Monnières en Loire-Atlantique`}
        description={shortDescription}
        imageUrl={logoUrl}
      >
        <script type="application/ld+json">{JSON.stringify(linkedData)}</script>
      </Seo>
  )
}

export default function AssociationPage({ data }) {
  const { name, category, shortDescription, logo, address, phones, contacts, email, website, socialNetworks, description } = data.strapiAssociation
  const image = getImage(logo?.localFile)

  return (
    <Layout>
      <PageLayout sx={category.color && { "--theme-ui-colors-primary": category.color }}>
        <Grid gap={2} sx={{ gridTemplate: ['"a" "b" "c"', null, '"a b" "c b" 1fr'] }}>
          <Box sx={{ gridArea: 'a' }}>
            <Badge bg={category.color}>{category.name}</Badge>
            <Heading as="h1" sx={{ mt: 3, mb: 1, fontSize: 5 }}>{name}</Heading>
            <Paragraph p={1}><Text as="em" color={category.color}>{shortDescription}</Text></Paragraph>
          </Box>
          <Box sx={{  gridArea: 'b', justifySelf: ['start', null, 'end'] }}>
            {image &&
              <Image sx={{display: 'block'}}
                width={image.width} height={image.height}
                src={image.images.fallback.src} srcSet={image.images.fallback.srcSet} sizes={image.images.fallback.sizes}
                alt=""
              />
            }
          </Box>
          <Box as="address" sx={{ gridArea: 'c', fontStyle: 'normal' }}>
            <Themed.h3>Coordonnées</Themed.h3>
            {address && coordinatesLine('Adresse', address)}
            {phones?.map((phone, index) => coordinatesLine(`Téléphone (${phone.name})`, (<Themed.a href={`tel:${phone.number}`}>{phone.number}</Themed.a>), index))}
            {contacts?.map((contact, index) => coordinatesLine(contact.role, contact.name, index))}
            {email && coordinatesLine('Email', (<Themed.a href={`mailto:${email}`}>{email}</Themed.a>))}
            {website && coordinatesLine('Site internet', (<Themed.a href={website}>{website}</Themed.a>))}
            {socialNetworks.map((socialNetwork, index) => coordinatesLine(socialNetwork.socialNetwork, (<Themed.a href={socialNetwork.url}>{socialNetwork.name}</Themed.a>), index))}
          </Box>
        </Grid>

        {description.data.childMarkdownRemark.html &&
            <BaseStyles dangerouslySetInnerHTML={{__html: description.data.childMarkdownRemark.html}}/>
        }

      </PageLayout>
    </Layout>
  )
}
